import { createReducer, on } from '@ngrx/store';
import {
  AchActions,
  CheckActions,
  ConfirmationPageActions,
  InternalTransferActions,
  InternalTransfersActions,
  ReviewPageActions,
  TransactionDetailsActions,
  TransactionFormActions,
  WireActions,
} from '../../create-request/actions';
import { Acknowledgement } from '../../create-request/models/review-page/ReviewPage';
import {
  StandingAuthAchActions,
  StandingAuthCheckActions,
  StandingAuthConfirmationPageActions,
  StandingAuthDetailsActions,
  StandingAuthInternalTransferActions,
  StandingAuthReviewPageActions,
  StandingAuthWireActions,
} from '../../standing-authorizations/actions';
import { ComponentState } from '../types/ComponentState';
import { ViewPageActions } from '../../transaction-view/actions';
import { LetterOfAuthorizationResponse } from '../../create-request/models/pdf/LetterOfAuthorizationResponse';
import { InitReleaseActions } from '../../initiate-and-release/actions';

export const reviewPageKey = 'reviewPage';

export interface State extends ComponentState {
  acknowledgement: Acknowledgement;
  acknowledgeCallFinished: boolean;
  submitClicked: boolean;
  pdf: string;
  pdfs: string[];
}

export const initialReviewPage: State = {
  loading: false,
  error: '',
  hasError: false,
  acknowledgement: {
    isAcknowledgementReviewed: false,
    acknowledgementConsentTime: '',
  },
  acknowledgeCallFinished: false,
  submitClicked: false,
  pdf: '',
  pdfs: []
};

export const reviewPageReducer = createReducer(
  initialReviewPage,
  on(ReviewPageActions.updateAcknowledgement, (state, { type, ...action }) => {
    const { acknowledgement } = action;
    return {
      ...state,
      acknowledgement: acknowledgement,
    };
  }),
  on(
    InternalTransferActions.callAcknowledge,
    InternalTransfersActions.callAcknowledgeList,
    AchActions.callAcknowledge,
    AchActions.callAddProfileAcknowledge,
    WireActions.callAcknowledgeV3,
    TransactionFormActions.callWireSloaVerifyV3FromCreateTransaction,
    TransactionFormActions.callWireSloaVerifyV3FromCreateTransactionCustomRulesSplit,
    TransactionFormActions.callInternalTransferVerifyCustomRule,
    CheckActions.callOnetimeAcknowledge,
    CheckActions.callRecurringAcknowledge,
    StandingAuthInternalTransferActions.callAcknowledge,
    StandingAuthCheckActions.callAcknowledge,
    StandingAuthWireActions.callAcknowledgeV3,
    StandingAuthAchActions.callAcknowledge,
    (state) => {
      return {
        ...state,
        loading: true,
        error: '',
        hasError: false,
        acknowledgeCallFinished: false,
        submitClicked: false,
      };
    }
  ),
  on(InitReleaseActions.callInitReleaseByDraftId, (state, {isSloa}) => {
    return {
      ...state,
      loading: true,
      error: '',
      hasError: false,
      acknowledgeCallFinished: false,
      submitClicked: false,
    };
  }),
  on(
    InternalTransferActions.acknowledgeCallSuccessful,
    InternalTransfersActions.acknowledgeListCallSuccessful,
    AchActions.acknowledgeCallSuccessful,
    WireActions.acknowledgeCallV3Successful,
    CheckActions.acknowledgeCallSuccessful,
    StandingAuthInternalTransferActions.acknowledgeCallSuccessful,
    StandingAuthCheckActions.acknowledgeCallSuccessful,
    StandingAuthWireActions.acknowledgeCallV3Successful,
    StandingAuthAchActions.acknowledgeCallSuccessful,
    (state) => {
      return {
        ...state,
        loading: false,
        error: '',
        hasError: false,
        acknowledgeCallFinished: true,
      };
    }
  ),
  on(InternalTransferActions.verifyCallFail,
    InternalTransferActions.verifyCallFinishedWithError,
    InternalTransferActions.verifyCallSuccessful, 
    InternalTransfersActions.verifyListCallFinishedWithError, 
    InternalTransfersActions.verifyListCallSuccessful, 
    InternalTransfersActions.verifyListCallFailed,
    InitReleaseActions.initReleaseByDraftIdFailed,
    WireActions.verifyV3CallSuccessful, 
    WireActions.verifyV3CallFailed,
    WireActions.callWireSloaVerifyV3FromCreateTransactionFailed,
    WireActions.callWireSloaVerifyV3FromCreateTransactionSuccessful,
    AchActions.verifyCallSuccessful,
    AchActions.verifyCallFailed,
    CheckActions.verifyCallSuccessful,
    CheckActions.verifyCallFailed,
    CheckActions.verifyCallFinishedWithError,
    StandingAuthInternalTransferActions.verifyCallFail,
    StandingAuthInternalTransferActions.verifyCallFinishedWithError,
    StandingAuthInternalTransferActions.verifyCallSuccessful, 
    StandingAuthWireActions.verifyV3CallFailed,
    StandingAuthWireActions.verifyV3CallSuccessful,
    StandingAuthCheckActions.verifyCallSuccessful,
    StandingAuthCheckActions.verifyCallFailed,
    StandingAuthCheckActions.verifyCallFinishedWithError,
    (state) => {
    return {
      ...state,
      loading: false,
      error: '',
      hasError: false,
    };
  }),
  on(
    InternalTransferActions.acknowledgeCallFail,
    AchActions.acknowledgeCallFail,
    WireActions.acknowledgeCallV3Fail,
    CheckActions.acknowledgeCallFailed,
    StandingAuthInternalTransferActions.acknowledgeCallFail,
    StandingAuthCheckActions.acknowledgeCallFailed,
    StandingAuthWireActions.acknowledgeCallV3Fail,
    StandingAuthAchActions.acknowledgeCallFail,
    (state, err) => {
      return {
        ...state,
        loading: false,
        error: err.message,
        hasError: true,
        errorList: err.error.errors ?? [],
        acknowledgeCallFinished: false,
      };
    }
  ),
  on(TransactionDetailsActions.loadWithdrawalDate,
    StandingAuthDetailsActions.loadWithdrawalDate, (state) => {
      return {
        ...state,
        loading: true
      }
    }),
  on(
    TransactionDetailsActions.loadWithdrawalDateSuccess,
    TransactionDetailsActions.loadWithdrawalDateFailure,
    StandingAuthDetailsActions.loadWithdrawalDateSuccess,
    StandingAuthDetailsActions.loadWithdrawalDateFailure,
    (state) => {
      return {
        ...state,
        loading: false
      }
    }
  ),
  on(
    StandingAuthAchActions.acknowledgeCallFinishedWithError,
    (state, err) => {
      return {
        ...state,
        loading: false,
        error: '',
        hasError: true,
        acknowledgeCallFinished: false,
      };
    }
  ),
  on(
    ConfirmationPageActions.viewTransactionStatusClicked,
    ConfirmationPageActions.createAnotherRequestClicked,
    StandingAuthConfirmationPageActions.createNewStandingAuthClicked,
    StandingAuthConfirmationPageActions.returnStandingAuthClicked,
    () => {
      return initialReviewPage;
    }
  ),
  on(
    ReviewPageActions.submitClicked,
    StandingAuthReviewPageActions.submitClicked,
    (state) => {
      return {
        ...state,
        submitClicked: true,
      };
    }
  ),
  on(
    ReviewPageActions.cancelClicked,
    StandingAuthReviewPageActions.cancelClicked,
    (state) => {
      return {
        ...state,
        submitClicked: false,
      };
    }
  ),
  on(
    TransactionFormActions.editLoadComplete,
    StandingAuthReviewPageActions.editCompleted,
    (state) => {
      return initialReviewPage;
    }
  ),
  on(ViewPageActions.backButtonClicked,
    ViewPageActions.backButtonClickedFromMsv,
    (state) => {
    return {
      ...state,
      pdf: ''
    }
  }),
  on(ReviewPageActions.generateAuthorizationFormSuccessful,
    StandingAuthReviewPageActions.generateAuthorizationFormSuccessful,
    ViewPageActions.generateAuthorizationFormStandingAuthSuccessful,
    (state, { type, ...action }) => {
      const { response } = action;
      return {
        ...state,
        pdf: response?.pdf ?? ''
      }
    }),
  on(ReviewPageActions.generateAuthorizationFormsSuccessful,
    (state, { type, ...action }) => {
      const { responses } = action;
      return {
        ...state,
        pdfs: [...(responses?.map((response: LetterOfAuthorizationResponse) => {
          return response.pdf
        })) ?? []
        ]
      }
    }),
);
